import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
    EBillsActions,
    GetBills,
    GetBillsSuccess,
    GetBillsFailure,
    DownloadBill,
    DownloadBillFailure,
    DownloadBillSuccess,
    DownloadFile
} from '../actions/bills.actions';
import { BillService } from '../../services/bill.service';
import { BillsPaginatorInterface } from '../../models/bills.paginator.interface';
import * as FileSaver from 'file-saver';
import { Store} from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractEffects } from '../../../shared/abstract.effects';

@Injectable()
export class BillsEffects extends AbstractEffects {

    billingId: number = null;
    filename: string = null;
    mimeType: string = null;
    fileId: number = null;
    fileName: string = null;
    fileTypeSlug: string = null;

    constructor(
        private actions: Actions,
        private billService: BillService,
        public override store: Store<any>
    ) {
        super(store);
    }

    getBills = createEffect(() => this.actions.pipe(
        ofType<GetBills>(EBillsActions.GetBills),
        switchMap(() => this.billService.get()),
        switchMap((response: BillsPaginatorInterface) => of(new GetBillsSuccess(response))),
        catchError((error: HttpErrorResponse) => {
            if (401 === error.status || 403 === error.status) {
                this.ResendRequestWithNewToken(new GetBills());
            } else {
                this.DisplayMessage('error_with_retry', 400, new GetBillsFailure());
            }
            return throwError(error);
        })
    ));

    downloadBill = createEffect(() => this.actions.pipe(
        ofType<DownloadBill>(EBillsActions.DownloadBill),
        tap(params => {
            this.billingId = params.billingId;
            this.filename = params.filename;
            this.mimeType = params.mimeType;
        }),
        switchMap(() => this.billService.getFileUrl(this.billingId, true)),
        tap((response: any) => {
            fetch(response.url)
                .then(res => res.blob()) // Gets the response and returns it as a blob
                .then(blob => {
                    FileSaver.saveAs(new Blob([blob], { type: this.mimeType }), this.filename);
                });
        }),
        switchMap(() => of(new DownloadBillSuccess())),
        catchError((error: HttpErrorResponse) => {
            if (401 === error.status || 403 === error.status) {
                this.ResendRequestWithNewToken(new DownloadBill(this.billingId, this.filename, this.mimeType));
            } else {
                this.DisplayMessage('error_download_bill', 400, new DownloadBillFailure());
            }
            return throwError(error);
        })
    ));

    downloadFile = createEffect(() => this.actions.pipe(
        ofType<DownloadFile>(EBillsActions.DownloadFile),
        tap(params => {
            this.fileId = params.fileId;
            this.mimeType = params.mimeType;
            this.fileName = params.fileName;
            this.fileTypeSlug = params.fileTypeSlug;
        }),
        switchMap(() => this.billService.downloadFile(this.fileId, this.fileTypeSlug)),
        tap((response: any) => {
            window.open(response.url, '_blank');
        }),
        switchMap(() => of(new DownloadBillSuccess())),
        catchError((error: HttpErrorResponse) => {
            if (401 === error.status || 403 === error.status) {
                this.ResendRequestWithNewToken(new DownloadFile(this.fileId, this.fileTypeSlug, this.mimeType, this.fileName));
            } else {
                this.DisplayMessage('error_download_file', 400, new DownloadBillFailure());
            }
            return throwError(error);
        })
    ));
}
